import { apiFetch } from '@ami/react/core';
import _ from 'lodash';
import { roundNumberToNearestCent } from '@ami/react/core';

class QuoteInstallationQuery {
  constructor(quote) {
    return {
      dealerBranchId: quote.step1.dealerBranchId,
      priceZoneId: quote.step1.priceZoneId,
      products: quote.products.map((product) => ({
        productId: product.productId,
        productOptionIds: product.productOptions
          .map((x) => x.productOptionId)
          .filter((x) => x),
      })),
    };
  }
}

class QuoteInstallationInformation {
  constructor(json) {
    this.installationOptions = json.installationOptions;
    this.products = json.products;
    this.installationLocations = json.installationLocations.map(
      (x) => new QuoteInstallationLocationOption(x)
    );
    this.basePlantLocation = json.basePlantLocation;
    this.priceZoneId = json.priceZoneId;
    this.dealerGroupId = json.dealerGroupId;
    this.isCurrent = json.isCurrent;
  }

  static async getData(query, quoteId) {
    const target = `quotes/${quoteId || 'current'}/installation-information`;
    var json = await apiFetch(target, 'POST', query, { automaticSuccessNotification: false });
    return json ? new QuoteInstallationInformation(json) : null;
  }
}

const quoteInstallationLocationType = Object.freeze({
  amiPlant: 'AmiPlant',
  dealerBranch: 'DealerBranch',
  other: 'Other',
});

class QuoteInstallationLocationOption {
  constructor(json) {
    this.id = json.id;
    this.name = json.name;
    this.label = json.name;
    this.location = json.location;
    this.type = json.type;
  }
}

class QuoteInstallationOptionSummary {
  constructor(json, discountPercentage) {
    this.installationOptionId = json.installationOptionId;
    this.name = json.name;
    this.partNumber = json.partNumber;
    this.description = json.description;
    this.msrp = json.msrp;
    this.dNetPrice = json.dNetPrice;
    this.discountPercentage = discountPercentage;
    this.quantity = 0;
  }

  increaseQuantity(increaseBy) {
    this.quantity = this.quantity + increaseBy;
  }

  getDealerDiscount() {
    return roundNumberToNearestCent(
      (this.dNetPrice * this.discountPercentage) / 100
    );
  }

  getUnitPrice() {
    return roundNumberToNearestCent(
      (this.dNetPrice * (100 - this.discountPercentage)) / 100
    );
  }

  getTotal() {
    return this.getUnitPrice() * this.quantity;
  }

  getTotalMsrp() {
    return this.msrp * this.quantity;
  }
}

class QuoteInstallationSummaryCalculator {
  constructor(quoteInstallation, discountPercentage) {
    this.quoteInstallation = quoteInstallation;
    this.discountPercentage = discountPercentage;
  }

  calculateFor(quote) {
    this._summaryEntries = _.values(
      _.groupBy(
        this.quoteInstallation.installationOptions,
        (x) => x.installationOptionId
      )
    ).map(
      (x) => new QuoteInstallationOptionSummary(x[0], this.discountPercentage)
    );
    this._products = this.quoteInstallation.products;
    quote.products.forEach((quoteProduct) =>
      this._calculateForProduct(quoteProduct)
    );
    return this._summaryEntries;
  }

  _increaseSummaryEntryQuantity(installationOptionId, increaseBy) {
    var entry = this._summaryEntries.find(
      (x) => x.installationOptionId === installationOptionId
    );
    if (!entry) return;
    entry.increaseQuantity(increaseBy);
  }

  _getInstallationProduct(quoteProduct) {
    const quotePoductOptionIds = quoteProduct.productOptions
      .filter((x) => !!x.productOptionId)
      .map((x) => x.productOptionId);
    return this._products.find((x) => {
      const installationPoductOptionIds = x.productOptions
        .filter((x) => !!x.productOptionId)
        .map((x) => x.productOptionId);
      return (
        x.productId === quoteProduct.productId &&
        ((installationPoductOptionIds.length === 0 &&
          quotePoductOptionIds.length === 0) ||
          (quotePoductOptionIds.length > 0 &&
            _.intersection(installationPoductOptionIds, quotePoductOptionIds)
              .length === quotePoductOptionIds.length))
      );
    });
  }

  _calculateForProduct(quoteProduct) {
    var installationProduct = this._getInstallationProduct(quoteProduct);
    if (!installationProduct) return;
    this._increaseSummaryEntryQuantity(
      installationProduct.installationOptionId,
      quoteProduct.quantity
    );
    installationProduct.productOptions.forEach((productOption) =>
      this._increaseSummaryEntryQuantity(
        productOption.installationOptionId,
        quoteProduct.quantity
      )
    );
  }
}

export {
  QuoteInstallationInformation,
  QuoteInstallationLocationOption,
  quoteInstallationLocationType,
  QuoteInstallationOptionSummary,
  QuoteInstallationQuery,
  QuoteInstallationSummaryCalculator,
};
